import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Pencil, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { horseAPI, crmAPI } from '../../../services/api';

const HorseInsuranceCard = ({ horse, canEditAccount, onUpdate, insuranceContacts, onEntitiesInvalidate }) => {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ insurance_carrier_id: '', insurance_agent_id: '', insurance_type: '' });

  // Create new contact dialog state
  const [createOpen, setCreateOpen] = useState(false);
  const [createFor, setCreateFor] = useState('carrier'); // 'carrier' | 'agent'
  const [newType, setNewType] = useState('organization');
  const [newName, setNewName] = useState('');
  const [newFirst, setNewFirst] = useState('');
  const [newLast, setNewLast] = useState('');

  const startEditing = () => {
    setForm({
      insurance_carrier_id: horse.insurance_carrier_id || '',
      insurance_agent_id:   horse.insurance_agent_id   || '',
      insurance_type:       horse.insurance_type        || '',
    });
    setIsEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const carrier = insuranceContacts.find(c => c.id === form.insurance_carrier_id);
      const agent   = insuranceContacts.find(c => c.id === form.insurance_agent_id);
      await horseAPI.update(horse.id, {
        insurance_carrier_id:   form.insurance_carrier_id || null,
        insurance_carrier_name: carrier?.system_name      || null,
        insurance_agent_id:     form.insurance_agent_id   || null,
        insurance_agent_name:   agent?.system_name        || null,
        insurance_type:         form.insurance_type        || null,
      });
      toast.success('Insurance updated');
      setIsEditing(false);
      onUpdate?.();
    } catch {
      toast.error('Failed to update insurance');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateContact = async () => {
    const isOrg = newType === 'organization';
    if (isOrg && !newName.trim())  { toast.error('Please enter an organization name'); return; }
    if (!isOrg && !newLast.trim()) { toast.error('Please enter a last name'); return; }
    setSaving(true);
    try {
      const payload = isOrg
        ? { crm_class: 'contact', crm_type: 'organization', system_name: newName.trim(), organization_name: newName.trim(), organization_industry_role: 'insurance' }
        : { crm_class: 'contact', crm_type: 'person', system_name: [newFirst.trim(), newLast.trim()].filter(Boolean).join(' '), person_first_name: newFirst.trim(), person_last_name: newLast.trim(), person_industry_role: 'insurance' };
      const response = await crmAPI.create(payload);
      const newId = response.data?.id || response.id;
      toast.success('Insurance contact created');
      setCreateOpen(false);
      setNewName(''); setNewFirst(''); setNewLast(''); setNewType('organization');
      onEntitiesInvalidate?.();
      setForm(prev => createFor === 'carrier'
        ? { ...prev, insurance_carrier_id: newId }
        : { ...prev, insurance_agent_id: newId }
      );
    } catch {
      toast.error('Failed to create insurance contact');
    } finally {
      setSaving(false);
    }
  };

  const openCreate = (forField) => { setCreateFor(forField); setCreateOpen(true); };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="text-base">Insurance</CardTitle>
          {canEditAccount && !isEditing && (
            <Button variant="ghost" size="icon" onClick={startEditing} data-testid="edit-insurance-btn">
              <Pencil className="w-4 h-4" />
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {isEditing ? (
            <div className="space-y-4">
              {/* Carrier */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Carrier</label>
                <Select value={form.insurance_carrier_id || '__none__'} onValueChange={v => setForm(f => ({ ...f, insurance_carrier_id: v === '__none__' ? '' : v }))}>
                  <SelectTrigger><SelectValue placeholder="Select carrier…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— None —</SelectItem>
                    {insuranceContacts.map(c => <SelectItem key={c.id} value={c.id}>{c.system_name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" className="w-full" onClick={() => openCreate('carrier')}>
                  <Plus className="w-4 h-4 mr-2" /> Create New Insurance Contact
                </Button>
              </div>
              {/* Agent */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Agent</label>
                <Select value={form.insurance_agent_id || '__none__'} onValueChange={v => setForm(f => ({ ...f, insurance_agent_id: v === '__none__' ? '' : v }))}>
                  <SelectTrigger><SelectValue placeholder="Select agent…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— None —</SelectItem>
                    {insuranceContacts.map(c => <SelectItem key={c.id} value={c.id}>{c.system_name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" className="w-full" onClick={() => openCreate('agent')}>
                  <Plus className="w-4 h-4 mr-2" /> Create New Insurance Contact
                </Button>
              </div>
              {/* Type */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Type</label>
                <Input value={form.insurance_type} onChange={e => setForm(f => ({ ...f, insurance_type: e.target.value }))} placeholder="e.g. Mortality, Major Medical" />
              </div>
            </div>
          ) : (
            <div className="space-y-2 text-sm">
              {(horse.insurance_carrier_id || horse.insurance_agent_id || horse.insurance_type) ? (
                <>
                  {horse.insurance_carrier_id && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground w-14 shrink-0">Carrier</span>
                      <button className="font-medium text-primary hover:underline text-left" onClick={() => navigate(`/crm/accounts/${horse.insurance_carrier_id}`)}>
                        {horse.insurance_carrier_name || insuranceContacts.find(c => c.id === horse.insurance_carrier_id)?.system_name || horse.insurance_carrier_id}
                      </button>
                    </div>
                  )}
                  {horse.insurance_agent_id && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground w-14 shrink-0">Agent</span>
                      <button className="font-medium text-primary hover:underline text-left" onClick={() => navigate(`/crm/accounts/${horse.insurance_agent_id}`)}>
                        {horse.insurance_agent_name || insuranceContacts.find(c => c.id === horse.insurance_agent_id)?.system_name || horse.insurance_agent_id}
                      </button>
                    </div>
                  )}
                  {horse.insurance_type && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground w-14 shrink-0">Type</span>
                      <span className="font-medium">{horse.insurance_type}</span>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-muted-foreground italic">No insurance information</p>
              )}
            </div>
          )}
          {isEditing && (
            <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
              <Button variant="outline" size="sm" onClick={() => setIsEditing(false)} disabled={saving}>Cancel</Button>
              <Button size="sm" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Insurance Contact Dialog */}
      <Dialog open={createOpen} onOpenChange={v => { setCreateOpen(v); if (!v) { setNewName(''); setNewFirst(''); setNewLast(''); setNewType('organization'); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Create New Insurance Contact</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex gap-2">
              <Button variant={newType === 'organization' ? 'default' : 'outline'} size="sm" onClick={() => setNewType('organization')} className={newType === 'organization' ? 'bg-primary text-white' : ''}>Organization</Button>
              <Button variant={newType === 'person' ? 'default' : 'outline'} size="sm" onClick={() => setNewType('person')} className={newType === 'person' ? 'bg-primary text-white' : ''}>Person</Button>
            </div>
            {newType === 'organization' ? (
              <div className="space-y-2">
                <label className="text-sm font-medium">Organization Name</label>
                <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="e.g. Great Plains Insurance" autoFocus />
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <label className="text-sm font-medium">First Name</label>
                  <Input value={newFirst} onChange={e => setNewFirst(e.target.value)} placeholder="First name" autoFocus />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Last Name *</label>
                  <Input value={newLast} onChange={e => setNewLast(e.target.value)} placeholder="Last name" />
                </div>
              </div>
            )}
            <p className="text-xs text-muted-foreground">This will create a new Insurance Contact in your Virtual Rolodex.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateContact} disabled={saving}>{saving ? 'Creating...' : 'Create Contact'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default HorseInsuranceCard;
